import type { ScreenTourMap, ScreenTourStep } from './types';

/** Pasos del formulario de tipo de préstamo (crear y editar comparten campos). */
const loanTypeFormSteps = (edit: boolean): ScreenTourStep[] => [
  { text: edit ? 'tours.loanTypeForm.welcomeEdit' : 'tours.loanTypeForm.welcomeNew' },
  { text: 'tours.loanTypeForm.name', target: 'loan-type-name' },
  { text: 'tours.loanTypeForm.frequency', target: 'loan-type-frequency' },
  { text: 'tours.loanTypeForm.days', target: 'loan-type-days' },
  { text: 'tours.loanTypeForm.fixed', target: 'loan-type-fixed' },
  // Solo se ve con «% fijo» activo.
  { text: 'tours.loanTypeForm.utility', target: 'loan-type-utility' },
  { text: 'tours.loanTypeForm.installments' },
  ...(edit ? [{ text: 'tours.loanTypeForm.editNote', target: 'loan-type-edit-note' } as ScreenTourStep] : []),
  { text: 'tours.loanTypeForm.save', target: 'save' },
];

const expenseTypeFormSteps = (edit: boolean): ScreenTourStep[] => [
  { text: edit ? 'tours.expenseTypeForm.welcomeEdit' : 'tours.expenseTypeForm.welcomeNew' },
  { text: 'tours.expenseTypeForm.name', target: 'expense-type-name' },
  { text: 'tours.expenseTypeForm.category', target: 'expense-type-category' },
  { text: 'tours.expenseTypeForm.operation', target: 'expense-type-operation' },
  { text: edit ? 'tours.expenseTypeForm.saveEdit' : 'tours.expenseTypeForm.saveNew', target: 'save' },
];

const bankFormSteps = (edit: boolean): ScreenTourStep[] => [
  { text: edit ? 'tours.bankForm.welcomeEdit' : 'tours.bankForm.welcomeNew' },
  { text: 'tours.bankForm.name', target: 'bank-name' },
  { text: 'tours.bankForm.country', target: 'bank-country' },
  { text: 'tours.bankForm.save', target: 'save' },
];

/**
 * Administrar (parte B): tipos de préstamo, motivos de gasto, bancos, permisos, dispositivos, centro de
 * alertas, configuración de rutas y liberar espacio.
 */
export const adminBTours: ScreenTourMap = {
  '/admin/loan-types': {
    key: 'screen-loan-types',
    guide: 'tipos-de-prestamo',
    steps: [
      { text: 'tours.loanTypes.welcome' },
      { text: 'tours.loanTypes.new', target: 'loan-type-new' },
      { text: 'tours.loanTypes.search', target: 'loan-types-search' },
      { text: 'tours.loanTypes.export', target: 'loan-types-export' },
      { text: 'tours.loanTypes.table', target: 'loan-types' },
      { text: 'tours.loanTypes.frequency', target: 'loan-types-col-frequency', whenMissing: 'center' },
      { text: 'tours.loanTypes.utility', target: 'loan-types-col-utility', whenMissing: 'center' },
      { text: 'tours.loanTypes.days', target: 'loan-types-col-days', whenMissing: 'center' },
      { text: 'tours.loanTypes.actions', target: 'loan-types-actions', whenMissing: 'center' },
      { text: 'tours.loanTypes.pagination', target: 'loan-types-pagination' },
    ],
  },
  '/admin/loan-types/new': { key: 'screen-loan-type-new', guide: 'tipos-de-prestamo', steps: loanTypeFormSteps(false) },
  '/admin/loan-types/:id/edit': { key: 'screen-loan-type-edit', guide: 'tipos-de-prestamo', steps: loanTypeFormSteps(true) },
  '/admin/loan-types/assign': {
    key: 'screen-loan-type-assign',
    guide: 'tipos-de-prestamo',
    steps: [
      { text: 'tours.loanTypeAssign.welcome' },
      { text: 'tours.loanTypeAssign.office', target: 'loan-type-assign-office' },
      { text: 'tours.loanTypeAssign.search', target: 'loan-type-assign-search' },
      { text: 'tours.loanTypeAssign.table', target: 'loan-type-assign' },
      { text: 'tours.loanTypeAssign.select', target: 'loan-type-assign-col-select', whenMissing: 'center' },
      { text: 'tours.loanTypeAssign.note' },
      { text: 'tours.loanTypeAssign.save', target: 'save' },
    ],
  },
  '/admin/expense-types': {
    key: 'screen-expense-types',
    steps: [
      { text: 'tours.expenseTypes.welcome' },
      { text: 'tours.expenseTypes.new', target: 'expense-types-new' },
      { text: 'tours.expenseTypes.search', target: 'expense-types-search' },
      { text: 'tours.expenseTypes.export', target: 'expense-types-export' },
      { text: 'tours.expenseTypes.table', target: 'expense-types' },
      { text: 'tours.expenseTypes.category', target: 'expense-types-col-category', whenMissing: 'center' },
      { text: 'tours.expenseTypes.operation', target: 'expense-types-col-operation', whenMissing: 'center' },
      { text: 'tours.expenseTypes.status', target: 'expense-types-col-status', whenMissing: 'center' },
      { text: 'tours.expenseTypes.actions', target: 'expense-types-actions', whenMissing: 'center' },
      { text: 'tours.expenseTypes.pagination', target: 'expense-types-pagination' },
    ],
  },
  '/admin/expense-types/new': { key: 'screen-expense-type-new', steps: expenseTypeFormSteps(false) },
  '/admin/expense-types/:id/edit': { key: 'screen-expense-type-edit', steps: expenseTypeFormSteps(true) },
  '/admin/banks': {
    key: 'screen-banks',
    steps: [
      { text: 'tours.banks.welcome' },
      { text: 'tours.banks.new', target: 'banks-new' },
      { text: 'tours.banks.search', target: 'banks-search' },
      { text: 'tours.banks.export', target: 'banks-export' },
      { text: 'tours.banks.table', target: 'banks' },
      { text: 'tours.banks.actions', target: 'banks-actions', whenMissing: 'center' },
      { text: 'tours.banks.pagination', target: 'banks-pagination' },
    ],
  },
  '/admin/banks/new': { key: 'screen-bank-new', steps: bankFormSteps(false) },
  '/admin/banks/:id/edit': { key: 'screen-bank-edit', steps: bankFormSteps(true) },
  '/admin/permissions': {
    key: 'screen-permissions',
    steps: [
      { text: 'tours.permissions.welcome' },
      { text: 'tours.permissions.scope' },
      { text: 'tours.permissions.role', target: 'permissions-role' },
      { text: 'tours.permissions.addRole', target: 'permissions-add-role' },
      { text: 'tours.permissions.module', target: 'permissions-module', whenMissing: 'center' },
      { text: 'tours.permissions.menu', target: 'permissions-menu', whenMissing: 'center' },
      { text: 'tours.permissions.selectAll', target: 'permissions-select-all' },
      { text: 'tours.permissions.save', target: 'permissions-save', whenMissing: 'center' },
      { text: 'tours.permissions.manageMenu', target: 'permissions-manage-menu' },
    ],
  },
  '/admin/permissions/new': {
    key: 'screen-menu-admin',
    steps: [
      { text: 'tours.menuAdmin.welcome' },
      { text: 'tours.menuAdmin.modules', target: 'menu-admin-modules' },
      { text: 'tours.menuAdmin.newModule', target: 'menu-admin-new-module' },
      { text: 'tours.menuAdmin.menus', target: 'menu-admin-menus' },
      { text: 'tours.menuAdmin.newMenu', target: 'menu-admin-new-menu' },
      { text: 'tours.menuAdmin.delete' },
    ],
  },
  '/admin/devices': {
    key: 'screen-devices',
    guide: 'dispositivos',
    steps: [
      { text: 'tours.devices.welcome' },
      { text: 'tours.devices.policy', target: 'devices-policy' },
      { text: 'tours.devices.tabs', target: 'devices-tabs' },
      { text: 'tours.devices.search', target: 'devices-search' },
      { text: 'tours.devices.table', target: 'devices' },
      { text: 'tours.devices.status', target: 'devices-col-status', whenMissing: 'center' },
      { text: 'tours.devices.approve', target: 'device-approve', whenMissing: 'center' },
      { text: 'tours.devices.block', target: 'device-block', whenMissing: 'center' },
      { text: 'tours.devices.more', target: 'device-more', whenMissing: 'center' },
      { text: 'tours.devices.refresh', target: 'devices-refresh' },
      { text: 'tours.devices.export', target: 'devices-export' },
    ],
  },
  '/admin/alerts': {
    key: 'screen-alerts',
    guide: 'centro-de-alertas',
    steps: [
      { text: 'tours.alerts.welcome' },
      { text: 'tours.alerts.summary', target: 'alerts-summary' },
      { text: 'tours.alerts.tabs', target: 'alerts-tabs' },
      { text: 'tours.alerts.kind', target: 'alerts-kind' },
      { text: 'tours.alerts.search', target: 'alerts-search' },
      { text: 'tours.alerts.table', target: 'alerts' },
      { text: 'tours.alerts.severity', target: 'alerts-col-severity', whenMissing: 'center' },
      { text: 'tours.alerts.what', target: 'alerts-col-what', whenMissing: 'center' },
      { text: 'tours.alerts.history', target: 'alert-history', whenMissing: 'center' },
      { text: 'tours.alerts.resolve', target: 'alert-resolve', whenMissing: 'center' },
      { text: 'tours.alerts.export', target: 'alerts-export' },
      { text: 'tours.alerts.refresh', target: 'alerts-refresh' },
    ],
  },
  '/admin/route-defaults': {
    key: 'screen-route-defaults',
    guide: 'configuracion-cobros',
    steps: [
      { text: 'tours.routeDefaults.welcome' },
      { text: 'tours.routeDefaults.intro', target: 'route-defaults-intro' },
      { text: 'tours.routeDefaults.loans', target: 'route-defaults-section-loans' },
      { text: 'tours.routeDefaults.fee', target: 'route-defaults-section-fee' },
      { text: 'tours.routeDefaults.records', target: 'route-defaults-section-records' },
      { text: 'tours.routeDefaults.app', target: 'route-defaults-section-app' },
      { text: 'tours.routeDefaults.save', target: 'route-defaults-save' },
      { text: 'tours.routeDefaults.apply', target: 'route-defaults-apply' },
    ],
  },
  '/admin/storage': {
    key: 'screen-storage',
    guide: 'liberar-espacio',
    steps: [
      { text: 'tours.storage.welcome' },
      { text: 'tours.storage.summary', target: 'storage-summary' },
      { text: 'tours.storage.months', target: 'storage-months' },
      { text: 'tours.storage.table', target: 'storage' },
      { text: 'tours.storage.select', target: 'storage-col-name', whenMissing: 'center' },
      { text: 'tours.storage.free', target: 'storage-free' },
      { text: 'tours.storage.history', target: 'storage-history', whenMissing: 'center' },
      { text: 'tours.storage.trash' },
    ],
  },
};
